import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Sparkles,
  Popcorn,
  Medal,
  Flower2,
  Flag,
  Music,
  Gamepad2,
  PiggyBank,
  Utensils,
  Home,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { trackBoxToggle } from '@/lib/reportBoxToggle';
import { toSpecialNameSlug } from '@/lib/reportAnalyticsSections';
import { getHashtagCategoryById, HASHTAG_CATEGORY_OPTIONS } from '../constants';
import { getFilledHashtagCategories, isFilledHashtag } from '../utils/hasContent';
import { useReportI18n } from '@/pages/reports/i18n';

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Sparkles,
  Popcorn,
  Medal,
  Flower2,
  Flag,
  Music,
  Gamepad2,
  PiggyBank,
  Utensils,
  Home,
};

export interface HashtagCategory {
  name?: string;
  color?: string;
  categoryId?: string;
  hashtags: Array<string | { text?: string; link?: string }>;
}

export interface CollapsibleHashtagsProps {
  title?: string;
  subtitle?: string;
  categories?: HashtagCategory[];
}

function resolveCategory(
  cat: HashtagCategory,
  t: (key: string, fallback?: string) => string
): { label: string; color: string; icon: React.ReactNode } {
  if (cat.categoryId) {
    const opt = getHashtagCategoryById(cat.categoryId);
    if (opt) {
      const IconComponent = CATEGORY_ICONS[opt.icon];
      return {
        label: t(`tiktokTrends.categories.${opt.id}`, opt.label),
        color: opt.color,
        icon: IconComponent ? <IconComponent className="w-3.5 h-3.5 shrink-0" /> : null,
      };
    }
  }
  const optByLabel = (cat.name && HASHTAG_CATEGORY_OPTIONS.find((o) => o.label === cat.name));
  const IconComponent = optByLabel ? CATEGORY_ICONS[optByLabel.icon] : null;
  return {
    label: optByLabel
      ? t(`tiktokTrends.categories.${optByLabel.id}`, optByLabel.label)
      : (cat.name ?? t('tiktokTrends.categories.geral')),
    color: cat.color ?? '#6b7280',
    icon: IconComponent ? <IconComponent className="w-3.5 h-3.5 shrink-0" /> : null,
  };
}

export const CollapsibleHashtags: React.FC<CollapsibleHashtagsProps> = ({
  title,
  subtitle,
  categories = [],
}) => {
  const { t } = useReportI18n();
  const heading = title || t('tiktokTrends.hashtags');
  const description = subtitle || t('tiktokTrends.hashtagsSubtitle');
  const [isOpen, setIsOpen] = useState(true);
  const boxSectionName = toSpecialNameSlug(heading);
  const filledCategories = getFilledHashtagCategories(categories);
  const normalizeHashtag = (tag: string | { text?: string; link?: string }) => {
    if (typeof tag === 'string') {
      const text = tag.trim();
      return text ? { text } : null;
    }
    const text = (tag.text ?? '').trim();
    if (!text) return null;
    const link = (tag.link ?? '').trim();
    return link ? { text, link } : { text };
  };

  if (filledCategories.length === 0) {
    return null;
  }

  return (
    <div className="w-full flex justify-center">
      <div
        className="w-full bg-white overflow-hidden"
        data-report-box
        data-report-box-section-name={boxSectionName}
      >
        <button
          type="button"
          onClick={(event) => {
            const next = !isOpen;
            setIsOpen(next);
            trackBoxToggle(next, event.currentTarget.parentElement);
          }}
          className="w-full flex items-center gap-2 bg-black text-white px-4 py-3 text-left hover:bg-gray-900 transition-colors font-['Times_New_Roman']"
        >
          {isOpen ? (
            <ChevronUp className="w-5 h-5 shrink-0" />
          ) : (
            <ChevronDown className="w-5 h-5 shrink-0" />
          )}
          <span className="text-[36px] leading-[40px] tracking-tight">{heading}</span>
        </button>
        {isOpen && (
          <div className="p-4 pt-2 space-y-4 font-['Times_New_Roman']">
            {description && (
              <p className="text-sm text-gray-600">{description}</p>
            )}
            <div className="space-y-10">
              {filledCategories.map((cat, idx) => {
                  const { label, color, icon } = resolveCategory(cat, t);
                  return (
                    <div key={idx} className="space-y-2">
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white font-["Times_New_Roman"]'
                        )}
                        style={{ backgroundColor: color }}
                      >
                        {icon}
                        {label}
                      </span>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-gray-900 font-['Times_New_Roman'] font-normal not-italic text-[38.34px] leading-none tracking-normal">
                        {(cat.hashtags || [])
                          .filter(isFilledHashtag)
                          .map(normalizeHashtag)
                          .filter(Boolean)
                          .map((tag, i) => {
                            const label = tag!.text.startsWith('#') ? tag!.text : `#${tag!.text}`;
                            return tag!.link ? (
                              <a
                                key={i}
                                href={tag!.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline"
                              >
                                {label}
                              </a>
                            ) : (
                              <span key={i}>{label}</span>
                            );
                          })}
                      </div>
                    </div>
                  );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
